import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/lifewestway-logo.png.asset.json";
import { makeSeoDescription } from "@/lib/seo-description";

const SITE = "https://lifewestway.com";
const TITLE = "Our Editorial Process | LifeWestWay";
const DESC =
  "How LifeWestWay researches, sources, reviews and updates its immigration, jobs, tax and finance guides — and who is accountable for them.";
const URL_ = `${SITE}/editorial-process`;
const IMAGE = `${SITE}${logoAsset.url}`;

const SOURCES = [
  { country: "United States", bodies: "USCIS, IRS, U.S. Department of State" },
  { country: "United Kingdom", bodies: "UK Visas and Immigration (UKVI), HMRC, GOV.UK" },
  { country: "Australia", bodies: "Department of Home Affairs, Australian Taxation Office (ATO)" },
  { country: "Italy", bodies: "Ministero degli Affari Esteri, Agenzia delle Entrate" },
  { country: "Portugal", bodies: "AIMA, Autoridade Tributária e Aduaneira" },
  { country: "Canada", bodies: "IRCC, Canada Revenue Agency (CRA)" },
];

export const Route = createFileRoute("/editorial-process")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:image", content: IMAGE },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
      { name: "twitter:image", content: IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/editorial-process" }],
  }),
  component: EditorialProcessPage,
});

function EditorialProcessPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link title="Return to the LifeWestWay homepage" to="/" className="hover:text-accent">
          Home
        </Link>
        <span className="px-2">/</span>
        <Link title="Learn about LifeWestWay and its editorial team" to="/about" className="hover:text-accent">
          About
        </Link>
        <span className="px-2">/</span>
        <span>Editorial Process</span>
      </nav>

      <h1 className="mt-4 font-display text-4xl font-bold text-foreground sm:text-5xl">
        Our Editorial Process
      </h1>
      <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground/75">
        We think it&apos;s fair for anyone reading LifeWestWay to know exactly how our content gets
        made before they rely on it for a decision as big as moving to another country. Here&apos;s
        the honest, unglamorous version of how it actually works.
      </p>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Who Writes This Content</h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          LifeWestWay is produced by a small editorial team working across immigration, tax, and
          personal finance content for six countries: the United States, United Kingdom, Australia,
          Italy, Portugal, and Canada. We don&apos;t claim to be immigration lawyers or licensed
          financial advisors, and we never will — that&apos;s precisely why we built this site the
          way we did, with heavy reliance on official sources and clear disclaimers rather than
          personal authority.
        </p>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          We use AI-assisted research and drafting as part of our workflow, the same way most modern
          publishers do. Every piece of content is reviewed against official sources before
          publication, not generated and posted without a check. We think being upfront about this
          matters more than pretending otherwise.
        </p>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">
          How We Research Every Article
        </h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          Before we publish a figure, a deadline, or a rule, we check it against the relevant
          government source, including:
        </p>
        <h3 className="mt-6 font-display text-lg font-semibold text-accent/90">
          Official sources by country
        </h3>
        <ul className="mt-4 divide-y divide-border rounded-2xl border border-border bg-card/60">
          {SOURCES.map((s) => (
            <li key={s.country} className="px-4 py-3 text-sm">
              <h4 className="font-display text-sm font-semibold text-foreground">{s.country}</h4>
              <span className="mt-1 block text-muted-foreground">{s.bodies}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          When a topic touches current policy debates or recent legislative changes — a budget
          announcement, a visa rule change, a new tax threshold — we prioritize the most recent
          official announcement over older secondary sources, and we note when a policy is still
          pending or under review rather than presenting it as settled.
        </p>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">
          Why This Matters More on This Site Than Most
        </h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          Immigration, tax, and personal finance fall into what the industry calls &quot;Your Money
          or Your Life&quot; (YMYL) content — topics where inaccurate information can cause real
          financial or legal harm. We take that seriously. It&apos;s why:
        </p>
        <ul className="mt-4 space-y-2 text-sm leading-7 text-foreground/80">
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>We cite official sources rather than asking you to simply trust our word</span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We flag clearly when a rule is genuinely complex or situation-dependent, rather than
              oversimplifying for a cleaner article
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We say &quot;consult a licensed professional&quot; when a decision is high-stakes or
              fact-specific — because that&apos;s honestly the right advice, not a legal disclaimer
              we&apos;re hiding behind
            </span>
          </li>
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">
          How Often We Update Content
        </h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          Immigration rules, tax thresholds, and visa fees change — sometimes every few months. Our
          review process includes:
        </p>
        <ul className="mt-4 space-y-2 text-sm leading-7 text-foreground/80">
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              Revisiting time-sensitive articles (visa fees, tax brackets, income thresholds) when
              we become aware of official changes
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              Adding a &quot;Last Updated&quot; note to articles covering figures that change
              frequently, so you can judge how current the information is
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              Correcting errors as soon as we identify them, whether through our own review or
              reader feedback
            </span>
          </li>
        </ul>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          We won&apos;t claim every article is checked on a fixed monthly schedule — realistically,
          high-traffic and time-sensitive pages get reviewed more often than a detailed guide about
          a niche visa category few people are researching in a given month. We think that&apos;s a
          more honest answer than promising a review cadence we can&apos;t actually guarantee.
        </p>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">
          Illustrative Case Studies: How We Handle Them
        </h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          Some articles include &quot;success stories&quot; or &quot;case study&quot; examples to
          help explain how a financial or immigration process might realistically unfold. These are{" "}
          <strong className="font-semibold text-foreground">
            composite, illustrative scenarios
          </strong>{" "}
          — built from realistic figures and common situations, not real, identifiable individuals —
          and we label them as such clearly, both at the start and end of each one. We think
          dressing these up as real testimonials would be misleading, so we don&apos;t.
        </p>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Corrections and Feedback</h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          We get things wrong sometimes — every publisher does, especially covering six
          countries&apos; worth of frequently changing rules. If you spot an error, an outdated
          figure, or something that doesn&apos;t match the official source, we want to know. Visit
          our{" "}
          <Link title="Contact the LifeWestWay editorial team" to="/contact" className="font-semibold text-accent hover:underline">
            Contact Us
          </Link>{" "}
          page and tell us specifically what you found — we read every message and prioritize
          factual corrections.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link title="Contact the LifeWestWay editorial team"
            to="/contact"
            className="inline-flex items-center rounded-md bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
          >
            Report a correction
          </Link>
          <Link title="See image and media credits for LifeWestWay"
            to="/media-credits"
            className="inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
          >
            Image licensing &amp; credits
          </Link>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">What We&apos;re Not</h2>
        <p className="mt-3 text-sm leading-7 text-foreground/80">
          To be completely clear about our limits:
        </p>
        <ul className="mt-4 space-y-2 text-sm leading-7 text-foreground/80">
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We are <strong className="font-semibold text-foreground">not</strong> a law firm,
              registered migration agency, or immigration consultancy
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              We are <strong className="font-semibold text-foreground">not</strong> a tax
              preparation service or financial advisory firm
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-accent">•</span>
            <span>
              Nothing on this site should replace advice from a licensed professional for your
              specific situation
            </span>
          </li>
        </ul>
        <p className="mt-4 text-sm leading-7 text-foreground/80">
          See our full{" "}
          <Link title="Read the LifeWestWay terms of use" to="/terms" className="font-semibold text-accent hover:underline">
            Disclaimer
          </Link>{" "}
          for more detail on what this means for how you should use our content.
        </p>
      </section>

      <section className="mt-12 rounded-2xl border border-accent/25 bg-card/60 p-5 sm:p-6">
        <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-accent">
          Disclaimer
        </h2>
        <p className="mt-2 text-sm leading-6 text-foreground/75">
          This page is for general informational purposes only and does not constitute legal, tax,
          immigration, or financial advice. Always verify current requirements with official
          government sources or a licensed professional. Have a question about how a specific
          article was researched, or want to flag something for review? Reach out through our{" "}
          <Link title="Contact the LifeWestWay editorial team" to="/contact" className="font-semibold text-accent hover:underline">
            Contact Us
          </Link>{" "}
          page.
        </p>
      </section>
    </div>
  );
}

